/**
 * College Expense Planning
 *
 * WP-RETIRE-13: Pure functions that turn what a parent actually knows — a
 * child's age, how many years of study, and the annual cost — into the
 * retirement events the projection engine already understands.
 *
 * This is an AUTHORING LAYER over the existing RetirementEvent primitive. It
 * introduces no new event type, table or endpoint: a college plan is an
 * ordinary `cash_flow` event with a negative amount spanning the tuition years,
 * and remains editable and deletable through the normal events panel.
 *
 * REAL TERMS (important)
 * ----------------------
 * `retirementCalculations.ts` is explicitly inflation-adjusted: its return
 * rates are documented as "real return after inflation". Amounts here are
 * therefore in TODAY'S dollars, and general inflation must NOT be applied —
 * doing so would double-count it and badly overstate the cost.
 *
 * What can legitimately be modelled is the amount by which college costs rise
 * FASTER than general inflation — a real escalation rate. That is exposed as a
 * user-controlled input defaulting to 0 rather than a number invented here.
 *
 * All money is integer cents.
 */
import type { RetirementEventCreate } from '@/types/retirement';

/** Age at which a child is assumed to start college, unless overridden. */
export const DEFAULT_COLLEGE_START_AGE = 18;

/** Typical length of an undergraduate course, used only as a form default. */
export const DEFAULT_YEARS_OF_STUDY = 4;

/**
 * Default REAL escalation of college costs, i.e. the amount by which they
 * outpace general inflation, expressed as a decimal per year.
 *
 * Deliberately 0. College costs have historically risen faster than general
 * inflation, but no specific figure is asserted here because none has been
 * verified for this codebase; inventing one would put a fabricated number into
 * a user's financial projection. The UI surfaces this as an adjustable
 * assumption so the user can apply their own figure.
 */
export const DEFAULT_REAL_COST_ESCALATION = 0;

/** Backend accepts years in this range (RetirementEventCreate validators). */
export const MIN_EVENT_YEAR = 2000;
export const MAX_EVENT_YEAR = 2100;

export interface CollegeChildInput {
  /**
   * Label for the event. Defaults to a generic "College - Child N" if blank:
   * a child's name is third-party PII about a minor and is only ever stored
   * because the user typed it.
   */
  label?: string;
  /** Child's current age. Ignored when startYear is given. */
  currentAge?: number | null;
  /** Explicit first year of college. Takes precedence over currentAge. */
  startYear?: number | null;
  /** Number of academic years to fund. */
  yearsOfStudy: number;
  /** Annual cost in TODAY'S dollars, in cents. Required — no default. */
  annualCostCents: number;
}

export interface CollegePlanOptions {
  /** Current calendar year. Injected so the functions stay pure/testable. */
  currentYear: number;
  /** Age at which college starts. */
  collegeStartAge?: number;
  /** Real (above-inflation) annual cost escalation, as a decimal. */
  realCostEscalationRate?: number;
}

/**
 * Derive the first year of college for a child.
 *
 * A child already at or past college age starts in the current year rather
 * than a past year — the projection cannot fund an expense that has been and
 * gone, and a past year would also fail the backend's year validator for
 * anyone planning far enough ahead.
 */
export function deriveCollegeStartYear(
  child: CollegeChildInput,
  options: CollegePlanOptions
): number {
  const { currentYear, collegeStartAge = DEFAULT_COLLEGE_START_AGE } = options;

  if (child.startYear != null) {
    return child.startYear;
  }

  if (child.currentAge == null) {
    return currentYear;
  }

  const yearsUntilCollege = collegeStartAge - child.currentAge;
  return currentYear + Math.max(0, yearsUntilCollege);
}

/**
 * Average annual cost over the study window, in today's dollars.
 *
 * With no real escalation this is simply the annual cost. With a positive real
 * rate, costs in later years are higher in real terms, so the window's average
 * is used: a `cash_flow` event carries ONE amount for the whole range, and the
 * average is the value that produces the correct total outflow.
 */
export function averageAnnualCostCents(
  annualCostCents: number,
  yearsUntilStart: number,
  yearsOfStudy: number,
  realCostEscalationRate: number
): number {
  if (yearsOfStudy <= 0) return 0;
  if (realCostEscalationRate === 0) return Math.round(annualCostCents);

  let total = 0;
  for (let i = 0; i < yearsOfStudy; i += 1) {
    const yearsFromNow = yearsUntilStart + i;
    total += annualCostCents * Math.pow(1 + realCostEscalationRate, yearsFromNow);
  }
  return Math.round(total / yearsOfStudy);
}

/**
 * Build the retirement events for one child's college costs.
 *
 * Returns an empty array when the input cannot produce a meaningful event
 * (no years of study, or no cost), rather than emitting a zero-value event
 * that would clutter the timeline.
 */
export function buildCollegeEventsForChild(
  child: CollegeChildInput,
  options: CollegePlanOptions,
  childIndex = 0
): RetirementEventCreate[] {
  const {
    currentYear,
    realCostEscalationRate = DEFAULT_REAL_COST_ESCALATION,
  } = options;

  if (child.yearsOfStudy <= 0 || child.annualCostCents <= 0) {
    return [];
  }

  const startYear = deriveCollegeStartYear(child, options);
  const endYear = startYear + child.yearsOfStudy - 1;

  // Outside the range the backend accepts; surfacing an empty result lets the
  // caller show a validation message rather than take a 422 from the API.
  if (
    startYear < MIN_EVENT_YEAR ||
    endYear > MAX_EVENT_YEAR ||
    endYear < startYear
  ) {
    return [];
  }

  const yearsUntilStart = Math.max(0, startYear - currentYear);
  const averageCost = averageAnnualCostCents(
    child.annualCostCents,
    yearsUntilStart,
    child.yearsOfStudy,
    realCostEscalationRate
  );

  if (averageCost <= 0) return [];

  const label = (child.label ?? '').trim() || `College - Child ${childIndex + 1}`;

  return [
    {
      name: label.slice(0, 100), // backend validator: 1-100 characters
      eventType: 'cash_flow',
      // Negative amount = expense. cash_flow REQUIRES endYear; lump_sum
      // requires it to be absent (backend model_post_init enforces both).
      amountCents: -Math.abs(averageCost),
      year: startYear,
      endYear,
      sortOrder: childIndex,
    },
  ];
}

/**
 * Build retirement events for every child in a college plan.
 *
 * Each child yields an independent event so they can be edited or removed
 * separately in the existing events panel.
 */
export function buildCollegeEvents(
  children: CollegeChildInput[],
  options: CollegePlanOptions
): RetirementEventCreate[] {
  return children.flatMap((child, index) =>
    buildCollegeEventsForChild(child, options, index)
  );
}

/**
 * Total real cost of a plan, for display. Sum of each child's average annual
 * cost multiplied by their years of study.
 */
export function totalCollegeCostCents(
  children: CollegeChildInput[],
  options: CollegePlanOptions
): number {
  return buildCollegeEvents(children, options).reduce((sum, event) => {
    const years = (event.endYear ?? event.year) - event.year + 1;
    return sum + Math.abs(event.amountCents) * years;
  }, 0);
}
